import React, { useState, useEffect, useRef } from "react";
import LoginPage from "./login_page";
import RegisterPage from "./register_page";
import classes from "./login_page.module.css";
import util from "../../styles/utilities.module.css";
import { useSearchParams, useNavigate, Navigate } from "react-router-dom";
import { useDispatch } from "react-redux";
import { setTokens, setUser, setLoginStatus } from "../../slices/auth";
import { useLoginMutation } from "../../slices/authApi";
import Spinner from "../UI/spinner/spinner";
import { setFfQuestions, setFormType, setFormData, setMcQuestions, setTfQuestions, setFbQuestions, setQuizID, setReadingPassage, setTextID } from "../../slices/form";
import { setAssignmentData, 
         setRubricData,
          setStudentWorkData,
          setAssignmentId,
          setRubricId,
          setSampleWorkId,
          setStudentWorkId,
          setGeneralFb,
          setRubricCriteriaFb,
          setCategoryFb,
          setOverallFb,
          setGradingCalibrationId,
          setGradingCalibrationData,
          setIssueCategories,
          updateTeacherRubricGradeData,
          setSampleWorkData,
          setOverviewShown,
          setSampleWorkInstructionsShown,
        } from "../../slices/gradingForm";

const AuthScreen = ({ isLoggedin, errorMsg: propErrorMsg }) => {

  const navigate = useNavigate();
  const dispatch = useDispatch();
  const errorRef = useRef(null);
  const [login, { isLoading }] = useLoginMutation();
  const [needRegister, setNeedRegister] = useState(false);
  const [errorMsg, setErrMsg] = useState(propErrorMsg || "");
  const [searchParams] = useSearchParams();
  const token = searchParams.get("token");
  const refreshToken = searchParams.get("refresh_token");

  const email = "help@classgenius.ai";

  const handleError = (msg) => {
    setErrMsg(msg);
  };

  useEffect(() => {
    if (propErrorMsg) {
      setErrMsg(propErrorMsg);
    }
  }, [propErrorMsg]);

  useEffect(() => {

    const handleLogin = async () => {
      try {
        const userData = await login().unwrap();
        dispatch(setUser({ userData }));
        if (userData.is_first_login) {
          setNeedRegister(true);
        } else if (userData.status !== "Active") {
          setErrMsg(
            "Your access has been denied. Please contact help@classgenius.ai"
          );
        } else {
          dispatch(setLoginStatus(true));
          const preserveData = localStorage.getItem('preserveData')
          if (JSON.parse(preserveData)) {
            const parsedData = JSON.parse(preserveData)
            if (parsedData.sourcePage === "questionsReview") {
              dispatch(setFfQuestions(parsedData.ffQuestions));
              dispatch(setMcQuestions(parsedData.mcQuestions));
              dispatch(setTfQuestions(parsedData.tfQuestions));
              dispatch(setFbQuestions(parsedData.fbQuestions));              
              dispatch(setFormData(parsedData.formData));
              dispatch(setTextID(parsedData.textID));
              dispatch(setQuizID(parsedData.quizID));
              dispatch(setReadingPassage(parsedData.readingPassage));
              navigate('/questionsReview');
            } else if (parsedData.sourcePage === "feedbackReview") {
              dispatch(setFormType({ formType: "grading"}));
              dispatch(setAssignmentData(parsedData.assignmentData));
              dispatch(setRubricData(parsedData.rubricData));
              dispatch(setSampleWorkData(parsedData.sampleWorkData));
              dispatch(setStudentWorkData(parsedData.studentWorkData));
              dispatch(setAssignmentId(parsedData.assignmentId));
              dispatch(setRubricId(parsedData.rubricId));
              dispatch(setSampleWorkId(parsedData.sampleWorkId));
              dispatch(setStudentWorkId(parsedData.studentWorkId));
              dispatch(setGeneralFb(parsedData.generalFb));
              dispatch(setRubricCriteriaFb(parsedData.rubricCriteriaFb));
              dispatch(setCategoryFb(parsedData.categoryFb));
              dispatch(setOverallFb(parsedData.overallFb));
              dispatch(setGradingCalibrationId(parsedData.gradingCalibrationId));
              dispatch(setGradingCalibrationData(parsedData.gradingCalibrationData));
              dispatch(setIssueCategories(parsedData.issue_categories));
              dispatch(updateTeacherRubricGradeData(parsedData.teacherRubricGrades));
              dispatch(setOverviewShown(parsedData.overviewShown));
              dispatch(setSampleWorkInstructionsShown(parsedData.sampleWorkInstructionsShown));              
              navigate('/grading/feedbackReview');
            }
          } else {
            navigate("/toolSelection");
          }
        }
      } catch (err) {
        if (!err?.status) {
          setErrMsg("No Server Response");
        } else if (err.status === 400) {
          setErrMsg("Missing Username or Password");
        } else if (err.status === 401) {
          setErrMsg("Unauthorized. Reach out to team@classgenius.ai for access.");
        } else {
          let err_message = "";
          if (err?.data?.message) {
            err_message = err?.data?.message;
          } else {
            err_message = err?.status + " Server Error ";
          }
  
          setErrMsg(err_message);
        }
      }
    };
  

    setErrMsg("");
    if (token && refreshToken) {
      dispatch(setTokens({ token, refreshToken }));
      handleLogin();
    }
  }, [dispatch, token, refreshToken, login, navigate]);

  useEffect(() => {
    if (errorMsg) {
      errorRef?.current?.scrollIntoView({ behavior: 'smooth' })
    }
  }, [errorMsg])

  if (isLoggedin) {
    return <Navigate to="/toolSelection" />;
  }

  if (isLoading) {
    return <Spinner />;
  }

  const displayErrorMsg = errorMsg || propErrorMsg;

  return (
    <>
      <section
        ref={errorRef}
        className={`${classes.authPage} auth-wrapper`}
        role="region"
        aria-label="login screen"
        data-testid="login-screen"
      >
        {displayErrorMsg && (
          <div className={`${util.alert} ${util['alert--danger']}`}>
            <h3>{displayErrorMsg} </h3>
            {displayErrorMsg === "Invalid access code" && (
              <div>
                <p>
                  Contact{" "}
                  <a
                    href={`mailto:${email}`}
                    target="_blank"
                    rel="noopener noreferrer"
                  >
                    {email}
                  </a>{" "}
                  for support.
                </p>
              </div>
            )}
          </div>
        )}
        {!needRegister ? (
          <LoginPage />
        ) : (
          <RegisterPage setError={handleError} />
        )}
      </section>
    </>
  );
};

export default AuthScreen;
